import { zodResolver } from "@hookform/resolvers/zod";
import { api } from "@slapinc/slappos-backend/api";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import type { GenericId as Id } from "convex/values";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
	Field,
	FieldError,
	FieldGroup,
	FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { StepPage } from "@/features/onboarding/_components/step-page";
import { handleConvexError } from "@/lib/errors";

const inviteFormSchema = z.object({
	teammates: z
		.array(
			z.object({
				email: z.email("Invalid email address").min(1, "Email is required"),
				roleId: z.string().min(1, "Select a role"),
			}),
		)
		.min(1),
});

type InviteFormData = z.infer<typeof inviteFormSchema>;

export const Route = createFileRoute("/onboarding/invite-team")({
	component: InviteTeamPage,
	validateSearch: (search: Record<string, unknown>) => ({
		org: search.org as string | undefined,
	}),
	beforeLoad: ({ search }) => {
		if (!search.org) {
			sessionStorage.setItem("__flash", "missing-org");
			throw redirect({ to: "/onboarding/create-organization", replace: true });
		}
	},
});

function InviteTeamPage() {
	const navigate = useNavigate();

	const roles = useQuery(api.features.roles.web.list, {});
	const createInvite = useMutation(api.features.invites.web.create);

	const {
		control,
		register,
		handleSubmit,
		formState: { isSubmitting, errors },
	} = useForm<InviteFormData>({
		resolver: zodResolver(inviteFormSchema),
		defaultValues: {
			teammates: [{ email: "", roleId: "" }],
		},
	});

	const { fields, append, remove } = useFieldArray({
		control,
		name: "teammates",
	});

	const onSubmit = async (data: InviteFormData) => {
		try {
			for (const teammate of data.teammates) {
				await createInvite({
					email: teammate.email,
					roleId: teammate.roleId as Id<"organizationRoles">,
				});
			}
			navigate({ to: "/" });
		} catch (error) {
			handleConvexError(error, "Failed to send invites");
		}
	};

	const handleSkip = () => {
		navigate({ to: "/" });
	};

	if (roles === undefined) {
		return (
			<StepPage title="Invite teammates" description="Loading roles...">
				<div className="flex justify-center py-8">
					<Spinner className="size-6" />
				</div>
			</StepPage>
		);
	}

	const roleOptions = roles ?? [];

	return (
		<StepPage
			title="Invite teammates"
			description="Invite your team to join your organization. You can always do this later."
		>
			<form onSubmit={handleSubmit(onSubmit)}>
				<FieldGroup>
					{fields.map((field, index) => (
						<div key={field.id} className="flex gap-2">
							<Field
								className="flex-1"
								data-invalid={!!errors.teammates?.[index]?.email}
							>
								{index === 0 && (
									<FieldLabel htmlFor={`email-${index}`}>Email</FieldLabel>
								)}
								<Input
									id={`email-${index}`}
									type="email"
									placeholder="colleague@example.com"
									aria-invalid={!!errors.teammates?.[index]?.email}
									{...register(`teammates.${index}.email`)}
								/>
								<FieldError>
									{errors.teammates?.[index]?.email?.message}
								</FieldError>
							</Field>

							<Field
								className="w-40"
								data-invalid={!!errors.teammates?.[index]?.roleId}
							>
								{index === 0 && (
									<FieldLabel htmlFor={`role-${index}`}>Role</FieldLabel>
								)}
								<Controller
									name={`teammates.${index}.roleId`}
									control={control}
									render={({ field: roleField }) => (
										<Select
											value={roleField.value}
											items={roleOptions.map((role) => ({
												label: role.name,
												value: role._id,
											}))}
											onValueChange={roleField.onChange}
										>
											<SelectTrigger
												className="w-full justify-between"
												aria-invalid={!!errors.teammates?.[index]?.roleId}
											>
												<SelectValue placeholder="Select role" />
											</SelectTrigger>
											<SelectContent>
												{roleOptions.map((role) => (
													<SelectItem key={role._id} value={role._id}>
														{role.name}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									)}
								/>
								<FieldError>
									{errors.teammates?.[index]?.roleId?.message}
								</FieldError>
							</Field>

							{fields.length > 1 && (
								<Button
									type="button"
									variant="ghost"
									size="sm"
									className={index === 0 ? "mt-7" : ""}
									onClick={() => remove(index)}
								>
									Remove
								</Button>
							)}
						</div>
					))}

					<Button
						type="button"
						variant="outline"
						onClick={() => append({ email: "", roleId: "" })}
						disabled={fields.length >= 5}
						className="w-full justify-center h-11 text-sm font-medium"
					>
						Add another teammate
					</Button>

					<Button
						type="submit"
						disabled={isSubmitting}
						className="w-full h-11 font-semibold"
					>
						{isSubmitting ? "Sending invites..." : "Continue"}
					</Button>

					<button
						type="button"
						onClick={handleSkip}
						className="mx-auto flex w-fit items-center gap-2 text-center text-sm font-medium text-neutral-500 transition-colors hover:text-neutral-900"
					>
						I'll do this later
					</button>
				</FieldGroup>
			</form>
		</StepPage>
	);
}
